import { Checkbox } from "design-system/atoms/Checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import { Plus } from "lucide-react"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import { TagNameSchema } from "schema"

type Option = {
  value: string
  label: string
  disabled?: boolean
}

type Props = {
  options: Option[]
  selectedValues: string[]
  onToggle: (value: string) => void
  onCreate?: (name: string) => void
  searchPlaceholder?: string
  children: ReactNode
}

export function TagsPicker({
  options,
  selectedValues,
  onToggle,
  onCreate,
  searchPlaceholder,
  children,
}: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")

  const parsedName = TagNameSchema.safeParse(searchQuery)
  const newName = parsedName.success ? parsedName.data : undefined
  const isExistingName = options.some(
    (option) => option.label.toLowerCase() === newName?.toLowerCase(),
  )
  const canCreate = !!onCreate && !!newName && !isExistingName

  function handleCreate() {
    if (!canCreate) return

    onCreate(newName)
    setSearchQuery("")
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="p-0 min-w-56" align="start">
        <Command>
          <CommandInput
            placeholder={
              searchPlaceholder ??
              t(onCreate ? "customers.tags.searchOrCreate" : "customers.tags.search")
            }
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {options.length > 0 && (
              <CommandEmpty className="p-4 text-center text-sm text-muted-foreground">
                {t("customers.tags.empty")}
              </CommandEmpty>
            )}
            {options.length === 0 && !canCreate && (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t(onCreate ? "customers.tags.emptyCreate" : "customers.tags.emptyNone")}
              </div>
            )}
            {canCreate && (
              <CommandGroup forceMount>
                <CommandItem
                  value={`__create__${newName}`}
                  onSelect={handleCreate}
                  className="gap-2"
                >
                  <Plus className="size-4" />
                  <span className="truncate">{t("customers.tags.create", { name: newName })}</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  disabled={option.disabled}
                  onSelect={() => onToggle(option.value)}
                  className="gap-2"
                >
                  <Checkbox checked={selectedValues.includes(option.value)} />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
